import { useCallback, useRef, useState, type ReactNode } from 'react'
import { connectorSyncsOnDemand } from '@app/shared'
import type { Connector } from '@app/shared'

// ============================================================================
// 1b · PULL TO REFRESH — docs/design/handoff-mobile/README.md §1b.
//
// "Refresh, and say honestly what is now stale."
//
// THE STRIP NAMES THE CONNECTORS. "Checking Canvas, Reddit and calendar…" —
// "Name the connectors — not 'Refreshing'." The names come off the connector list
// the app actually has, so a connector added or removed changes this sentence
// without anyone editing it, and a build with none says so instead of naming
// three that are not there.
//
// THE PULL IS THE FINGER'S, 1:1, and it only arms at the top of the scroll
// region — a pull that fights the list is a list that cannot be scrolled. The
// resistance below 1.0 is deliberate and is the ONE place in this port where a
// gesture is not 1:1: a pull-down has no target to land on, so the damping is
// what tells the finger it is approaching a threshold rather than dragging a
// sheet. (The swipe-to-complete gesture on 1g is 1:1 and must stay that way.)
// ============================================================================

/** How far the finger must pull past the top before releasing triggers a refresh. */
export const PULL_THRESHOLD = 74

/** Past this the strip stops following the finger at all — a pull, not a stretch. */
const PULL_MAX = 120

/** The finger moves this much; the strip moves that fraction of it. */
const RESISTANCE = 0.55

export type PullPhase = 'idle' | 'pulling' | 'refreshing'

/**
 * 'Canvas, Reddit and calendar' — an Oxford-free English list of what is about
 * to be checked. Planned connectors are excluded: a connector that has never
 * been connected is not something a pull can check.
 */
export function connectorNames(connectors: Connector[] | undefined): string | null {
  const live = (connectors ?? []).filter(connectorSyncsOnDemand).map((c) => c.shortName)
  if (live.length === 0) return null
  if (live.length === 1) return live[0]!
  return `${live.slice(0, -1).join(', ')} and ${live[live.length - 1]}`
}

export function PullToRefresh({
  connectors,
  onRefresh,
  children,
}: {
  connectors: Connector[] | undefined
  /** Runs the real pull. Resolves when every connector has answered, or failed. */
  onRefresh: () => Promise<void>
  children: ReactNode
}) {
  const [phase, setPhase] = useState<PullPhase>('idle')
  const [distance, setDistance] = useState(0)
  const from = useRef<number | null>(null)
  const scroller = useRef<HTMLDivElement>(null)

  const run = useCallback(async () => {
    setPhase('refreshing')
    setDistance(PULL_THRESHOLD)
    try {
      await onRefresh()
    } finally {
      setPhase('idle')
      setDistance(0)
    }
  }, [onRefresh])

  const names = connectorNames(connectors)

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {distance > 0 && (
        <div
          className="flex flex-none flex-col items-center justify-center gap-2 overflow-hidden bg-pull"
          style={{ height: distance }}
          role="status"
        >
          <span
            className={[
              'size-[26px] rounded-full border-[2.5px] border-line-2 border-t-ink',
              phase === 'refreshing' ? 'motion-safe:animate-spin' : '',
            ].join(' ')}
            aria-hidden
          />
          {distance > 44 && (
            <span className="px-4 text-center text-[11.5px] font-medium leading-none text-ink-3">
              {phase === 'refreshing'
                ? names
                  ? `Checking ${names}…`
                  : 'Checking — no connectors are set up yet…'
                : distance >= PULL_THRESHOLD
                  ? 'Release to check'
                  : 'Pull to check'}
            </span>
          )}
        </div>
      )}

      <div
        ref={scroller}
        className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-y-contain"
        onPointerDown={(e) => {
          if (phase === 'refreshing') return
          // Only from the top. Anywhere else the gesture belongs to the list.
          if ((scroller.current?.scrollTop ?? 0) > 0) return
          from.current = e.clientY
        }}
        onPointerMove={(e) => {
          if (from.current === null) return
          const dy = e.clientY - from.current
          if (dy <= 0) {
            setDistance(0)
            return
          }
          setPhase('pulling')
          setDistance(Math.min(PULL_MAX, dy * RESISTANCE))
        }}
        onPointerUp={() => {
          if (from.current === null) return
          from.current = null
          if (distance >= PULL_THRESHOLD) void run()
          else {
            setPhase('idle')
            setDistance(0)
          }
        }}
        onPointerCancel={() => {
          from.current = null
          setPhase('idle')
          setDistance(0)
        }}
      >
        {children}
      </div>
    </div>
  )
}
